// MX4 coach profiles (owner direction, Sept 30): a deep, personal introduction for each coach so
// members feel they already know who they'll meet at their first MX4 session.
// Copy is drawn from each coach's own published bio. Personal details (off the floor, signature
// move, class soundtrack) are only shown once the coach supplies them; never invent them.
// Coaches without a published bio show their photo with "Profile coming soon" (profile: false).
// `tone` varies the card colors (red, white or gray) so each profile stands apart.
import { COACHES } from './coaches';

const bio = (slug) => COACHES.find((coach) => coach.slug === slug);
const withMX4 = (classes) => (classes.includes('MX4') ? classes : ['MX4', ...classes]);

export const MX4_COACHES = [
  {
    slug: 'andy',
    name: 'Andy',
    pronoun: 'her',
    tone: 'red',
    title: 'Fitness Director · MX4 Coach',
    hook: 'Positive to encourage, happy to inspire, and grateful for the chance to help you change your life.',
    highlights: ['15+ years in fitness', '14 years at Delta Valley', '6 certifications'],
    firstSession:
      "Andy has spent more than 15 years in fitness and 14 of them at Delta Valley, so your first MX4 class is in experienced hands. Expect a warm welcome and encouragement from the very first interval. Her whole approach comes down to three words: positive, happy and grateful. She believes fitness is a lifestyle everyone is capable of and deserving of, so whatever level you walk in with is exactly the right place to start.",
    style: [
      ['Positive', 'She encourages every rep, so you keep moving when the interval gets hard.'],
      ['Happy', 'Her energy is meant to inspire, and it carries the whole room through the circuit.'],
      ['Grateful', 'She treats every session as a chance to help you feel better, physically and mentally.'],
    ],
    bestFor: [
      'First-timers and anyone coming back after a break.',
      'Members who want a mental reset as much as a physical one.',
      'Anyone who wants to feel better about themselves, inside and out.',
    ],
    quote: 'Working through physical challenges in the gym strengthens not only our muscles but also our resolve to conquer everyday stresses and challenges.',
    personal: { offTheFloor: null, signatureMove: null, soundtrack: null },
    classes: bio('andy').classes,
    certifications: bio('andy').certifications,
  },
  {
    slug: 'pam',
    name: 'Pam',
    pronoun: 'her',
    tone: 'white',
    title: 'Yoga Instructor · MX4 Coach',
    hook: 'A lifelong teacher who gives you the space to grow safely, in body and mind.',
    highlights: ['Lifelong teacher', 'Yoga Works 200-hour certified', 'CPR / First Aid / AED'],
    firstSession:
      "Pam has spent the greater part of her life teaching, and it shows from the moment class begins. Expect clear, patient guidance and room to find your footing. She wants every member to feel safe growing in their physical body and their mental one, and her greatest joy is watching people dig deep into who they are and branch out into something new.",
    style: [
      ['Patient', 'A lifetime of teaching means instruction you can follow from the very first rep.'],
      ['Safe', 'She gives you the space to grow safely, physically and mentally.'],
      ['Joyful', 'Sharing what she loves with others is her greatest joy, and it fills the room.'],
    ],
    bestFor: [
      'Newcomers who want clear, step-by-step coaching.',
      'Members who want body and mind to grow together.',
      'Anyone ready to branch out and try something new.',
    ],
    quote: 'It is my greatest joy to share my passions with others as they dig deep into who they are and branch out into something new.',
    personal: { offTheFloor: 'Teaching children in her classroom, playing golf, water skiing, or simply having fun with her family.', signatureMove: null, soundtrack: null },
    classes: withMX4(bio('pam').classes),
    certifications: bio('pam').certifications,
  },
  { slug: 'tina', name: 'Tina', pronoun: 'her', tone: 'gray', title: 'MX4 Coach', profile: false },
  {
    slug: 'john',
    name: 'John',
    pronoun: 'him',
    tone: 'red',
    title: 'Strength & Conditioning · MX4 Coach',
    hook: 'Forty-plus years of training, and still happiest when exercise becomes part of your every day.',
    highlights: ['40+ years of training', 'Bodybuilding contest winner', 'Law enforcement career'],
    firstSession:
      "John's love of fitness started at home, following calisthenics off a record album jacket with his mom and flexing next to his dad's plastic-coated weights. Since then he has managed a large commercial gym, won a tested bodybuilding contest and completed a career in law enforcement. In your first MX4 class, expect disciplined, experienced coaching from someone who most wants to see you keep moving long after the session ends.",
    style: [
      ['Disciplined', 'Competition and a career in law enforcement shaped how he trains, and how he coaches.'],
      ['Experienced', 'More than 40 years of strength and cardiovascular training stand behind every cue.'],
      ['Invested', 'He cares most about making exercise part of your life every day.'],
    ],
    bestFor: [
      'Members who want to build real strength.',
      'Anyone who thrives on structure and discipline.',
      'People making exercise a lifelong habit.',
    ],
    quote: 'I enjoy helping people achieve their fitness goals, but moreover, I enjoy seeing people continue to make physical activity and exercise a part of their lives every day.',
    personal: { offTheFloor: null, signatureMove: null, soundtrack: null },
    classes: withMX4(bio('john').classes),
    certifications: bio('john').certifications,
  },
  {
    slug: 'ty',
    name: 'Ty',
    pronoun: 'him',
    tone: 'white',
    title: 'MX4 Coach',
    hook: 'Your effort shapes your outcome, and Ty is there to show you how much you have in you.',
    highlights: ['MX4 certified', 'Strength training'],
    firstSession:
      "Ty believes your potential is already inside you, and the work is what brings it out. In your first MX4 class he will guide you to find more than you thought you had, with a steady reminder that your effort directly shapes your results. Hard work and dedication are the whole idea, and he will be right there with you for both.",
    style: [
      ['Driven', 'He believes your effort directly shapes your outcome.'],
      ['Empowering', 'He guides members to realize their full potential.'],
      ['Dedicated', 'Hard work and dedication sit at the heart of his coaching.'],
    ],
    bestFor: [
      'Members ready to push past what they think they can do.',
      'Anyone who wants a coach focused on effort and results.',
      'Strength-minded members who love to work hard.',
    ],
    quote: null,
    personal: { offTheFloor: null, signatureMove: null, soundtrack: null },
    classes: bio('ty').classes,
    certifications: bio('ty').certifications,
  },
  { slug: 'cheri', name: 'Cheri', pronoun: 'her', tone: 'gray', title: 'MX4 Coach', profile: false },
  {
    slug: 'jenn-l',
    name: 'Jenn',
    pronoun: 'her',
    tone: 'red',
    title: 'Personal Trainer · MX4 Coach',
    hook: 'Good music, clear goals, and a workout you will actually look forward to.',
    highlights: ['5 certifications', 'ACE Personal Trainer'],
    firstSession:
      "Jenn believes physical and mental health are closely connected, and that the workouts that last are the ones you enjoy. Expect good music, clear goals and a coach who wants you reaching levels you didn't think were possible. She isn't interested in quick fixes; she's here to help you make healthy living a lifestyle.",
    style: [
      ['Upbeat', 'Good music and real enjoyment keep you coming back.'],
      ['Goal-driven', 'She helps you set goals, then crush them.'],
      ['Whole-person', 'She treats physical and mental health as one priority.'],
    ],
    bestFor: [
      'Members who want lasting change, not a quick fix.',
      'Anyone who trains harder when they are having fun.',
      'Goal-setters ready to reach new levels.',
    ],
    quote: "If we enjoy what we are doing then we will find time for it and be motivated to excel to new levels we didn't think were possible.",
    personal: { offTheFloor: null, signatureMove: null, soundtrack: null },
    classes: bio('jenn-l').classes,
    certifications: bio('jenn-l').certifications,
  },
  { slug: 'lyndsey', name: 'Lyndsey', pronoun: 'her', tone: 'white', title: 'MX4 Coach', profile: false },
];

// MX4 coaches whose pictures are still coming, and substitute coaches (names only).
export const MX4_COACHES_PICTURE_SOON = ['Jamir', 'Lisa', 'David', 'Gianna'];
export const MX4_SUBS = ['Debbie', 'Heidi'];
